// ============================================================================
// Affirmations EN LETTRES (moteur v50, refonte lot 6.51) : « (x − 3)² = x² − 6x + 9 »,
// « 2(x + 4) − x = x + 8 », « a³ × a³ × a⁴ = a¹⁰ ». Une affirmation sans valeur proposée
// (`at`) dont les membres ont des lettres est une IDENTITÉ : vraie si les deux membres
// sont égaux pour toutes les valeurs des lettres (forme normale du noyau).
//
// Ce module dit, pour la feuille d'affirmations (kernel-claims.ts) :
//   - si deux écritures en lettres sont égales (`letterSame`) ;
//   - si une écriture est une VALEUR (développée, réduite : « x² − 6x + 9 », « 3x », « a¹⁰ ») —
//     deux valeurs différentes sont deux expressions différentes, c'est ce qui tranche ;
//   - le geste d'un maillon « a = b » (`letterGesture`) : développer, enlever des
//     parenthèses, calculer / réduire, règles des puissances — jugé comme sur une feuille
//     « développer et réduire » du noyau (kernel-sheet.ts, forme `expanded`), pour que les
//     deux feuilles nomment les mêmes pas de la même façon ;
//   - un contre-exemple (`letterCounterexample`) quand une ligne est fausse.
// Pur ; un petit cache garde les jugements des maillons.
// ============================================================================

import { createKernelSheetState, kernelJudge, kernelLineAtoms } from './kernel-sheet';
import type { ScratchModel } from './model';
import * as Q from './kernel/exact';
import {
  compareTerms,
  evalExact,
  normalize,
  polyIsConstant,
  polyRat,
  polyScale,
  polyToTerm,
} from './kernel/normal';
import { parseStatement, symbolsOf, termText, type Term } from './kernel/term';
import { constShapeKey, formulaText } from './kernel-formula';
import { powDebt } from './kernel-form';

const key = (t: Term): string => constShapeKey(t);

/** les lettres d'une écriture (π n'en est pas une) */
export function letterNames(t: Term): string[] {
  return [...symbolsOf(t)].filter((n) => n !== 'π').sort();
}
export const hasLetters = (t: Term): boolean => letterNames(t).length > 0;

/** deux écritures égales pour toutes les valeurs des lettres (null : indécis) */
export function letterSame(a: Term, b: Term): boolean | null {
  const c = compareTerms(a, b);
  return c === 'equal' ? true : c === 'different' ? false : null;
}

/** la forme développée et réduite d'une écriture POLYNOMIALE (null : pas un polynôme) */
export function expandedOf(t: Term): Term | null {
  const n = normalize(t);
  if (!n.ok || !polyIsConstant(n.rf.d)) return null;
  const d = polyRat(n.rf.d);
  if (!d || Q.isZero(d)) return null;
  const inv = Q.div(Q.ONE, d);
  if (!inv) return null;
  return polyToTerm(polyScale(n.rf.n, inv));
}

/**
 * Le signe d'un produit écrit : « −6x » se lit −1 × (6 × x) ; on le replie en (−6) × x,
 * pour comparer l'écriture à la forme réduite. Seul −1 se replie : « 2 × 3x » garde ses
 * deux nombres (un calcul reste à faire).
 */
function signFold(t: Term): Term {
  switch (t.k) {
    case 'add':
      return { k: 'add', c: t.c.map(signFold) };
    case 'mul': {
      let fs = t.c.map(signFold).flatMap((f) => (f.k === 'mul' ? f.c : [f]));
      const nums = fs.filter((f): f is Extract<Term, { k: 'num' }> => f.k === 'num');
      const minus = nums.find((n) => Q.eq(n.v, Q.rat(-1)));
      if (nums.length === 2 && minus) {
        const other = nums.find((n) => n !== minus)!;
        fs = fs.filter((f) => f !== minus && f !== other);
        fs.unshift({ k: 'num', v: Q.neg(other.v) });
      }
      return fs.length === 1 ? fs[0]! : { k: 'mul', c: fs };
    }
    case 'pow':
    case 'div':
    case 'binom':
      return { ...t, a: signFold(t.a), b: signFold(t.b) };
    case 'fn':
      return { ...t, a: signFold(t.a) };
    default:
      return t;
  }
}

/**
 * Une VALEUR en lettres : l'écriture développée et réduite, rien à calculer
 * (« x² − 6x + 9 », « 12 − x », « 3x », « a¹⁰ ») ; « x × x », « 2 × 3x », « x + 2x »,
 * « (x + 5)(x + 5) », « x¹ » ne le sont pas.
 */
export function isLetterValue(t: Term): boolean {
  if (!hasLetters(t)) return false;
  // moteur v53 : un quotient de monômes réduit (« 1 ÷ a³ », « a⁴ ÷ b⁴ »)
  if (isMonomialQuotient(t)) return true;
  const e = expandedOf(t);
  if (!e) return false;
  return key(signFold(t)) === key(signFold(e));
}

/* --------------------- fractions en lettres (moteur v53, lot 6.57) --------------------- */
// Les règles des puissances sur un QUOTIENT (« a⁵ ÷ a⁸ », « a⁴ ÷ b⁴ ») : la forme normale y
// voit une fraction rationnelle, pas un polynôme ; ces gestes sont jugés ici.
//
//   Règles des puissances   a⁵ ÷ a⁸ → a^(5 − 8) (ou a⁻³, 1 ÷ a³), a⁶ ÷ a³ → a³,
//                           a⁻² ÷ a⁻² → 1, a⁻³ → 1 ÷ a³, a⁰ → 1,
//                           a⁴ ÷ b⁴ ↔ (a ÷ b)⁴                                 1 HORA
//   Calculer                a^(5 − 8) → a⁻³ (l'exposant)                       1 HORA

/** un entier rationnel écrit (« 5 », « −2 ») ; null sinon */
function intRat(t: Term): Q.Rat | null {
  return t.k === 'num' && Q.isInteger(t.v) ? t.v : null;
}

/** une lettre à une puissance entière écrite (« a », « a⁵ », « a^(−2) ») */
function letterPow(t: Term): { x: string; e: Q.Rat } | null {
  if (t.k === 'sym' && t.n !== 'π') return { x: t.n, e: Q.ONE };
  if (t.k === 'pow' && t.a.k === 'sym' && t.a.n !== 'π') {
    const e = intRat(t.b);
    return e ? { x: t.a.n, e } : null;
  }
  return null;
}

/** un monôme de lettres : lettres distinctes à des puissances entières ≥ 1 (« a³b », « a ») */
function letterMonomial(t: Term): string[] | null {
  const fs = t.k === 'mul' ? t.c : [t];
  const xs: string[] = [];
  for (const f of fs) {
    const p = letterPow(f);
    if (!p || Q.cmp(p.e, Q.ONE) < 0) return null;
    // « a¹ » n'est pas réduit
    if (f.k === 'pow' && Q.eq(p.e, Q.ONE)) return null;
    if (xs.includes(p.x)) return null;
    xs.push(p.x);
  }
  return xs;
}

/** « 1 ÷ a³ », « a⁴ ÷ b⁴ », « a ÷ b² » : un quotient de monômes sans lettre commune */
function isMonomialQuotient(t: Term): boolean {
  if (t.k !== 'div') return false;
  const den = letterMonomial(t.b);
  if (!den) return false;
  if (t.a.k === 'num') return Q.eq(t.a.v, Q.ONE);
  const num = letterMonomial(t.a);
  return !!num && !num.some((x) => den.includes(x));
}

const same = (a: Term, b: Term): boolean => key(a) === key(b);

/** le geste d'un maillon sur un quotient de puissances (moteur v53) ; null sinon */
export function letterQuotientGesture(a: Term, b: Term): LetterGesture | null {
  // a^(m − n) → a^k : on calcule l'exposant
  if (a.k === 'pow' && a.b.k === 'add' && b.k === 'pow' && same(a.a, b.a)) {
    const parts = a.b.c.map(intRat);
    const e = intRat(b.b);
    if (e && parts.every((x) => x !== null)) {
      const sum = parts.reduce((s, x) => Q.add(s, x!), Q.ZERO);
      if (Q.eq(sum, e) && a.a.k === 'sym' && a.a.n !== 'π') return 'compute';
    }
  }
  // a⁻ᵏ → 1 ÷ aᵏ ; a⁰ → 1
  const pa = letterPow(a);
  if (pa && a.k === 'pow') {
    if (Q.sign(pa.e) < 0 && b.k === 'div' && b.a.k === 'num' && Q.eq(b.a.v, Q.ONE)) {
      const pb = letterPow(b.b);
      if (pb && pb.x === pa.x && Q.eq(pb.e, Q.neg(pa.e))) return 'power';
    }
    if (Q.isZero(pa.e) && b.k === 'num' && Q.eq(b.v, Q.ONE)) return 'power';
  }
  if (a.k === 'div') {
    // aᵐ ÷ aⁿ (même lettre)
    const p = letterPow(a.a);
    const q = letterPow(a.b);
    if (p && q && p.x === q.x) {
      const k = Q.sub(p.e, q.e);
      // aᵐ ÷ aⁿ → a^(m − n), l'exposant écrit
      if (b.k === 'pow' && b.a.k === 'sym' && b.a.n === p.x && b.b.k === 'add') {
        const parts = b.b.c.map(intRat);
        if (
          parts.length === 2 &&
          parts.every((x) => x !== null) &&
          Q.eq(parts[0]!, p.e) &&
          Q.eq(parts[1]!, Q.neg(q.e))
        )
          return 'power';
      }
      const pb = letterPow(b);
      if (pb && pb.x === p.x && Q.eq(pb.e, k) && !Q.isZero(k)) return 'power';
      if (Q.isZero(k) && ((b.k === 'num' && Q.eq(b.v, Q.ONE)) || (pb && Q.isZero(pb.e))))
        return 'power';
      if (Q.sign(k) < 0 && b.k === 'div' && b.a.k === 'num' && Q.eq(b.a.v, Q.ONE)) {
        const d = letterPow(b.b);
        if (d && d.x === p.x && Q.eq(d.e, Q.neg(k))) return 'power';
      }
    }
    // Aⁿ ÷ Bⁿ → (A ÷ B)ⁿ
    if (a.a.k === 'pow' && a.b.k === 'pow' && b.k === 'pow' && b.a.k === 'div') {
      const n = intRat(a.a.b);
      if (n && same(a.a.b, a.b.b) && same(b.b, a.a.b) && same(b.a.a, a.a.a) && same(b.a.b, a.b.a))
        return 'power';
    }
  }
  // (A ÷ B)ⁿ → Aⁿ ÷ Bⁿ
  if (a.k === 'pow' && a.a.k === 'div' && b.k === 'div' && b.a.k === 'pow' && b.b.k === 'pow') {
    const n = intRat(a.b);
    if (n && same(b.a.b, a.b) && same(b.b.b, a.b) && same(b.a.a, a.a.a) && same(b.b.a, a.a.b))
      return 'power';
  }
  return null;
}

/** des écritures (affirmations, corrigé) avec un quotient de monômes « 1 ÷ a³ » (moteur v53) */
export function textsUseMonomialQuotients(texts: readonly string[]): boolean {
  const has = (t: Term): boolean => {
    if (isMonomialQuotient(t)) return true;
    switch (t.k) {
      case 'add':
      case 'mul':
        return t.c.some(has);
      case 'div':
      case 'pow':
      case 'binom':
        return has(t.a) || has(t.b);
      case 'fn':
        return has(t.a);
      default:
        return false;
    }
  };
  for (const line of texts) {
    for (const side of line.split(/[=<>≤≥≠]/)) {
      const p = parseStatement(side.trim());
      if (p.ok && p.s.k === 'expr' && has(p.s.e)) return true;
    }
  }
  return false;
}

/** Le corrigé se sert-il d'un geste sur un quotient de puissances en lettres ? (moteur v53) */
export function pathUsesLetterQuotients(path: readonly string[]): boolean {
  for (const line of path) {
    if (/[<>≤≥]/.test(line)) continue;
    const ms = line.split('=').map((t) => {
      const p = parseStatement(t.trim());
      return p.ok && p.s.k === 'expr' ? p.s.e : null;
    });
    if (ms.length < 2 || ms.some((m) => !m)) continue;
    for (let i = 0; i + 1 < ms.length; i++)
      if (letterQuotientGesture(ms[i]!, ms[i + 1]!)) return true;
  }
  return false;
}

/* ------------------------------ éléments neutres ------------------------------ */

const isNumV = (t: Term, v: number): boolean => t.k === 'num' && Q.eq(t.v, Q.rat(v));

/** x × 1, 1 × x, 0 × x, x¹, x + 0 : l'écriture une fois ces calculs faits */
function neutralFold(t: Term): Term {
  switch (t.k) {
    case 'add': {
      const c = t.c.map(neutralFold).filter((u) => !isNumV(u, 0));
      return c.length === 0 ? { k: 'num', v: Q.ZERO } : c.length === 1 ? c[0]! : { k: 'add', c };
    }
    case 'mul': {
      const c = t.c.map(neutralFold);
      if (c.some((u) => isNumV(u, 0))) return { k: 'num', v: Q.ZERO };
      const k = c.filter((u) => !isNumV(u, 1));
      return k.length === 0 ? { k: 'num', v: Q.ONE } : k.length === 1 ? k[0]! : { k: 'mul', c: k };
    }
    case 'pow': {
      const a = neutralFold(t.a);
      const b = neutralFold(t.b);
      if (isNumV(b, 1)) return a;
      return { k: 'pow', a, b };
    }
    case 'div':
    case 'binom':
      return { ...t, a: neutralFold(t.a), b: neutralFold(t.b) };
    case 'fn':
      return { ...t, a: neutralFold(t.a) };
    default:
      return t;
  }
}

/* ------------------------------ gestes ------------------------------ */

export type LetterGesture =
  | 'expand'
  | 'parens'
  | 'compute'
  | 'neutral'
  | 'power'
  | 'powerExpand'
  | 'fraction'
  | 'factor';

/** (s)ⁿ écrit en produit de n facteurs s (« (x − 3)² = (x − 3)(x − 3) ») */
function powerAsProduct(a: Term, b: Term): boolean {
  if (a.k !== 'pow' || a.b.k !== 'num' || b.k !== 'mul') return false;
  const n = Q.ratToNumber(a.b.v);
  if (!Number.isInteger(n) || n < 2 || n > 6 || b.c.length !== n) return false;
  const ka = key(a.a);
  return b.c.every((f) => key(f) === ka);
}

const cache = new Map<string, LetterGesture | null>();
const CACHE_MAX = 2000;

/** un nom de grandeur qui n'est pas une lettre des écritures */
function freeName(a: Term, b: Term): string {
  const used = new Set([...letterNames(a), ...letterNames(b)]);
  return ['T', 'S', 'R', 'Z', 'W'].find((n) => !used.has(n)) ?? 'Ω';
}

/**
 * Le pas d'un maillon « a = b » (deux écritures égales, l'une au moins en lettres), jugé
 * comme sur une feuille « développer et réduire » : null si ce n'est pas UN pas (trop d'un
 * coup, ou en sens inverse — factoriser, réordonner).
 */
export function letterGesture(a: Term, b: Term): LetterGesture | null {
  const k = `${key(a)}→${key(b)}`;
  if (cache.has(k)) return cache.get(k)!;
  const g = computeGesture(a, b);
  if (cache.size >= CACHE_MAX) cache.clear();
  cache.set(k, g);
  return g;
}

/** une feuille « développer » (ou « factoriser ») du noyau, le temps de juger un maillon */
function sheetModel(form: 'expanded' | 'factored', T: string, a: Term, answer: Term): ScratchModel {
  const ans = `${T} = ${termText(answer)}`;
  return {
    symbols: {},
    goal: [T],
    kernel: {
      plugin: 'algebra',
      goal: { kind: 'simplify', target: T },
      form,
      start: `${T} = ${termText(a)}`,
      answer: ans,
      path: [ans],
    },
  } as unknown as ScratchModel;
}

/** le libellé du pas « T = a » → « T = b » sur cette feuille (null : pas un pas) */
function sheetStep(model: ScratchModel, T: string, b: Term): string | null {
  try {
    const ks = createKernelSheetState(model);
    const atoms = kernelLineAtoms(`${T} = ${termText(b)}`);
    const v = kernelJudge(
      ks,
      atoms.map((x) => ({ atoms: [x] })),
      { verify: true },
    );
    return v.kind === 'step' ? (v.rule?.label ?? '') : null;
  } catch {
    return null;
  }
}

function computeGesture(a: Term, b: Term): LetterGesture | null {
  if (key(neutralFold(a)) === key(b)) return 'neutral';
  // moteur v53 : les règles des puissances sur un quotient
  const q = letterQuotientGesture(a, b);
  if (q) return q;
  if (powerAsProduct(a, b)) return 'powerExpand';
  const exp = expandedOf(a);
  if (!exp) return null;
  const T = freeName(a, b);
  // une somme écrite en produit : c'est factoriser (« (x − 3)(x + 2) + 4(x − 3) →
  // (x − 3)(x + 2 + 4) »), jamais développer
  if (a.k === 'add' && productOfSums(b)) {
    const f = sheetStep(sheetModel('factored', T, a, b), T, b);
    return f !== null && /^(Factoriser|Faire apparaître|Écrire les carrés)/.test(f)
      ? 'factor'
      : null;
  }
  const label = sheetStep(sheetModel('expanded', T, a, exp), T, b);
  if (label !== null) {
    // développer distribue ; « 5 × (2x + x) → 5 × 3x » réduit la parenthèse (moins de
    // termes, aucun nouveau) : c'est réduire
    if (label.startsWith('Développer'))
      return addends(b) < addends(a) && termCount(b) <= termCount(a) ? 'compute' : 'expand';
    if (label.startsWith('Enlever les parenthèses')) return 'parens';
    if (label.startsWith('Règles des puissances')) return 'power';
    if (label.startsWith('Simplifier la fraction')) return 'fraction';
    // « a³ × a³ × a⁴ → a⁶ × a⁴ », « x × x → x² » : les règles des puissances seules
    if (powDebt(b) < powDebt(a) && termCount(a) === termCount(b)) return 'power';
    return 'compute';
  }
  // en sens inverse : factoriser (« (x − 3)(x + 2) + 4(x − 3) → (x − 3)(x + 2 + 4) »)
  const f = sheetStep(sheetModel('factored', T, a, b), T, b);
  if (f !== null && /^(Factoriser|Faire apparaître|Écrire les carrés)/.test(f)) return 'factor';
  return null;
}

/** un produit dont un facteur est une somme (« (x − 3)(x + 6) », « (x + 5)² ») */
function productOfSums(t: Term): boolean {
  if (t.k === 'pow') return t.a.k === 'add';
  return t.k === 'mul' && t.c.some((f) => f.k === 'add' || (f.k === 'pow' && f.a.k === 'add'));
}

/** le nombre de termes d'une somme (1 pour un produit, un nombre, une lettre) */
const termCount = (t: Term): number => (t.k === 'add' ? t.c.length : 1);
/** les termes de toutes les sommes de l'écriture, parenthèses comprises */
function addends(t: Term): number {
  switch (t.k) {
    case 'add':
      return t.c.length + t.c.reduce((n, c) => n + addends(c), 0);
    case 'mul':
      return t.c.reduce((n, c) => n + addends(c), 0);
    case 'pow':
    case 'div':
    case 'binom':
      return addends(t.a) + addends(t.b);
    case 'fn':
      return addends(t.a);
    default:
      return 0;
  }
}

/* ------------------------------ contre-exemple ------------------------------ */

const TRIES = [1, 2, 3, -1, 4, 5, 10, -2];

/**
 * Des valeurs des lettres pour lesquelles a et b n'ont pas la même valeur (une ligne
 * fausse : « (x − 3)² = x² − 9 » — pour x = 1, 4 et −8).
 */
export function letterCounterexample(
  a: Term,
  b: Term,
): { at: string; va: string; vb: string } | null {
  const names = [...new Set([...letterNames(a), ...letterNames(b)])];
  if (!names.length || names.length > 3) return null;
  for (let j = 0; j < TRIES.length; j++) {
    const env: Record<string, Q.Rat> = {};
    names.forEach((n, i) => (env[n] = Q.rat(TRIES[(j + i * 2) % TRIES.length]!)));
    let va: Q.Rat | null = null;
    let vb: Q.Rat | null = null;
    try {
      va = evalExact(a, env);
      vb = evalExact(b, env);
    } catch {
      continue;
    }
    if (!va || !vb || Q.eq(va, vb)) continue;
    const at = names.map((n) => `${n} = ${formulaText({ k: 'num', v: env[n]! })}`).join(', ');
    return {
      at,
      va: formulaText({ k: 'num', v: va }),
      vb: formulaText({ k: 'num', v: vb }),
    };
  }
  return null;
}
